import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import StatusBadge from '../shared/StatusBadge';

function calcJoursOuvres(startDate, endDate) {
  const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const feriesFixes = (y) => [`${y}-01-01`,`${y}-05-01`,`${y}-05-08`,`${y}-07-14`,`${y}-08-15`,`${y}-11-01`,`${y}-11-11`,`${y}-12-25`];
  const easterDate = (y) => {
    const a=y%19,b=Math.floor(y/100),c=y%100,d=Math.floor(b/4),e=b%4,f=Math.floor((b+8)/25),g=Math.floor((b-f+1)/3),h=(19*a+b-d-g+15)%30,i=Math.floor(c/4),k=c%4,l=(32+2*e+2*i-h-k)%7,m=Math.floor((a+11*h+22*l)/451),month=Math.floor((h+l-7*m+114)/31),day=((h+l-7*m+114)%31)+1;
    return new Date(y,month-1,day);
  };
  const feries = (y) => {
    const easter=easterDate(y),lundi=new Date(easter),asc=new Date(easter),pent=new Date(easter);
    lundi.setDate(easter.getDate()+1); asc.setDate(easter.getDate()+39); pent.setDate(easter.getDate()+50);
    return new Set([...feriesFixes(y),localISO(lundi),localISO(asc),localISO(pent)]);
  };
  const start = new Date(startDate), end = new Date(endDate);
  if (end < start) return '';
  const years = new Set([start.getFullYear(), end.getFullYear()]);
  const joursFeeries = new Set([...[...years].flatMap(y => [...feries(y)])]);
  let count = 0;
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay(), iso = localISO(cur);
    if (dow !== 0 && dow !== 6 && !joursFeeries.has(iso)) count++;
    cur.setDate(cur.getDate()+1);
  }
  return String(count);
}

export default function ConventionDetail({ conventionId, onBack, onEdit, onNewStage }) {
  const [convention, setConvention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectComment, setRejectComment] = useState('');
  const [showReject, setShowReject] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualFile, setManualFile] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [editFields, setEditFields] = useState({});
  const [remindSuccess, setRemindSuccess] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const load = () => {
    api.get(`/conventions/${conventionId}`)
      .then(res => setConvention(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [conventionId]);

  const action = async (fn) => {
    setActionLoading(true);
    setError('');
    try {
      await fn();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteConvention = async () => {
    setActionLoading(true);
    setError('');
    try {
      await api.delete(`/conventions/${conventionId}`);
      onBack();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
      setActionLoading(false);
    }
  };

  const validate = () => action(() => api.post(`/conventions/${conventionId}/validate`));
  const sign = () => action(() => api.post(`/conventions/${conventionId}/sign`));
  const cancelSign = () => action(() => api.post(`/conventions/${conventionId}/cancel-sign`));
  const archive = () => action(() => api.post(`/conventions/${conventionId}/archive`));
  const newStage = async () => {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post(`/conventions/${conventionId}/new-stage`);
      if (onNewStage) onNewStage(res.data.id);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création du nouveau stage');
      setActionLoading(false);
    }
  };
  const remind = async () => {
    setActionLoading(true);
    setError('');
    setRemindSuccess('');
    try {
      const res = await api.post(`/conventions/${conventionId}/remind`);
      setRemindSuccess(res.data.message);
      setTimeout(() => setRemindSuccess(''), 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la relance');
    } finally {
      setActionLoading(false);
    }
  };

  const syncSignatures = async () => {
    setSyncLoading(true);
    setSyncMsg('');
    try {
      const res = await api.post(`/conventions/${conventionId}/sync-signatures`);
      setConvention(res.data.convention);
      setSyncMsg(res.data.updated > 0 ? `${res.data.updated} signature(s) mise(s) à jour` : 'Déjà à jour');
      setTimeout(() => setSyncMsg(''), 4000);
    } catch (err) {
      setSyncMsg(err.response?.data?.error || 'Erreur lors de la synchronisation');
      setTimeout(() => setSyncMsg(''), 4000);
    } finally {
      setSyncLoading(false);
    }
  };

  const openEdit = () => {
    const schedRaw = c.schedule;
    const schedText = typeof schedRaw === 'string' ? schedRaw : (schedRaw ? Object.entries(schedRaw).map(([j, v]) => `${j}: ${[v?.matin, v?.apmidi].filter(Boolean).join(', ')}`).join(' | ') : '');
    setEditFields({
      schedule: schedText,
      companyEmail: c.company?.email || '',
      companyPhone: c.company?.phone || '',
      tutorFirstName: c.tutorFirstName || '',
      tutorName: c.tutorName || '',
      tutorEmail: c.tutorEmail || '',
      tutorPhone: c.tutorPhone || '',
      repFirstName: c.repFirstName || '',
      repName: c.repName || '',
      repPhone: c.repPhone || '',
      weeklyHours: c.weeklyHours || '',
      totalDays: c.totalDays || (c.startDate && c.endDate ? calcJoursOuvres(c.startDate, c.endDate) : ''),
    });
    setShowEdit(true);
  };

  const saveEdit = () => action(async () => {
    await api.put(`/conventions/${conventionId}`, editFields);
    setShowEdit(false);
  });
  const reject = () => action(async () => {
    if (!rejectComment.trim()) throw { response: { data: { error: 'Un commentaire est requis' } } };
    await api.post(`/conventions/${conventionId}/reject`, { comment: rejectComment });
    setShowReject(false);
    setRejectComment('');
  });
  const signManual = () => action(async () => {
    if (!manualFile) throw { response: { data: { error: 'Sélectionnez un fichier PDF' } } };
    const formData = new FormData();
    formData.append('file', manualFile);
    await api.post(`/conventions/${conventionId}/sign-manual`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setShowManual(false);
    setManualFile(null);
  });

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/conventions/${conventionId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `convention_${conventionId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const blob = err.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        try { setError(JSON.parse(text).error); } catch { setError('Erreur lors du téléchargement du PDF'); }
      } else {
        setError('Erreur lors du téléchargement du PDF');
      }
    }
  };

  if (loading) return <div className="text-gray-500 text-sm py-8">Chargement...</div>;
  if (!convention) return <div className="text-gray-500 text-sm">Convention introuvable</div>;

  const c = convention;
  const canValidate = c.status === 'SUBMITTED';
  const canSign = c.status === 'VALIDATED';
  const canCancelSign = c.status === 'SIGNING';
  const canReject = ['SUBMITTED', 'VALIDATED'].includes(c.status);
  const canPdf = ['VALIDATED', 'SIGNING', 'SIGNED', 'ARCHIVED'].includes(c.status);
  const canArchive = c.status === 'SIGNED';
  const canManual = ['VALIDATED', 'SIGNING'].includes(c.status);
  const canEditForm = ['DRAFT', 'SUBMITTED', 'VALIDATED', 'REJECTED'].includes(c.status);
  const canNewStage = ['SIGNED', 'ARCHIVED'].includes(c.status);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
        <h2 className="text-xl font-semibold text-gray-800">
          Convention — {c.student?.firstName} {c.student?.lastName}
        </h2>
        <StatusBadge status={c.status} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-3 flex-wrap items-center">
          {canValidate && (
            <button
              onClick={validate}
              disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Valider
            </button>
          )}
          {canSign && (
            <button
              onClick={sign}
              disabled={actionLoading}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Lancer les signatures (DocuSeal)
            </button>
          )}
          {canPdf && (
            <button
              onClick={downloadPdf}
              className="border border-gray-300 hover:border-gray-400 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Télécharger PDF
            </button>
          )}
          {canArchive && (
            <button
              onClick={archive}
              disabled={actionLoading}
              className="border border-gray-300 hover:border-gray-400 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Archiver
            </button>
          )}
          {canNewStage && (
            <button
              onClick={newStage}
              disabled={actionLoading}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Nouveau stage
            </button>
          )}
          {canCancelSign && (
            <button
              onClick={cancelSign}
              disabled={actionLoading}
              className="border border-red-300 hover:border-red-400 text-red-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Annuler les signatures
            </button>
          )}
          {canManual && (
            <button
              onClick={() => setShowManual(!showManual)}
              className="border border-orange-200 hover:border-orange-400 text-orange-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Signature papier
            </button>
          )}
          {canReject && (
            <button
              onClick={() => setShowReject(!showReject)}
              className="border border-red-200 hover:border-red-400 text-red-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Rejeter
            </button>
          )}
          {canEditForm && onEdit && (
            <button
              onClick={() => onEdit(conventionId)}
              className="border border-blue-300 hover:border-blue-500 text-blue-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              Modifier la convention
            </button>
          )}
          <button
            onClick={openEdit}
            className="border border-gray-300 hover:border-gray-400 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Modifier les contacts
          </button>
        </div>
        <button
          onClick={() => { setShowDelete(true); setDeleteConfirm(''); }}
          className="border border-red-300 hover:border-red-500 text-red-500 hover:text-red-700 text-sm font-medium px-4 py-2 rounded-lg transition-colors shrink-0"
        >
          Supprimer la convention
        </button>
      </div>

      {/* État des signatures */}
      {['SIGNING', 'SIGNED', 'ARCHIVED'].includes(c.status) && (
        <div className="mb-6">
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Étudiant',   date: c.signatureDateEtudiant },
              { label: 'Entreprise', date: c.signatureDateEntreprise },
              { label: 'Enseignant', date: c.signatureDateEnseignant },
              { label: 'Proviseur',  date: c.signatureDateProviseur },
            ].map(({ label, date }) => (
              <div
                key={label}
                className={`rounded-xl border p-3 text-center ${
                  date
                    ? 'bg-green-50 border-green-200'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
                {date ? (
                  <>
                    <p className="text-green-600 text-xl leading-none">✓</p>
                    <p className="text-xs text-green-700 mt-1">{new Date(date).toLocaleDateString('fr-FR')}</p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-400 text-xl leading-none">⏳</p>
                    <p className="text-xs text-gray-400 mt-1">En attente</p>
                  </>
                )}
              </div>
            ))}
          </div>
          {c.status === 'SIGNING' && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                onClick={remind}
                disabled={actionLoading}
                className="text-sm px-3 py-1.5 rounded border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 disabled:opacity-50"
              >
                Relancer les signataires en attente
              </button>
              <button
                onClick={syncSignatures}
                disabled={syncLoading}
                className="text-sm px-3 py-1.5 rounded border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 flex items-center gap-1.5"
              >
                <span className={syncLoading ? 'animate-spin inline-block' : ''}>↻</span>
                Rafraîchir depuis DocuSeal
              </button>
              {remindSuccess && <span className="text-sm text-green-600">{remindSuccess}</span>}
              {syncMsg && <span className={`text-sm ${syncMsg.includes('Erreur') ? 'text-red-600' : 'text-blue-600'}`}>{syncMsg}</span>}
            </div>
          )}
        </div>
      )}

      {/* Mode dégradé — signature papier */}
      {showManual && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-orange-800 mb-1">Signature papier scannée</p>
          <p className="text-xs text-orange-600 mb-3">Importez le PDF de la convention signée à la main par toutes les parties.</p>
          <input
            type="file"
            accept="application/pdf"
            onChange={e => setManualFile(e.target.files[0])}
            className="block text-sm text-gray-600 mb-3 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:bg-orange-100 file:text-orange-700"
          />
          <div className="flex gap-2">
            <button
              onClick={signManual}
              disabled={actionLoading || !manualFile}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg"
            >
              Valider comme signé
            </button>
            <button onClick={() => { setShowManual(false); setManualFile(null); }} className="text-sm text-gray-600 px-3 py-2">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Formulaire de rejet */}
      {showReject && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-red-800 mb-2">Commentaire de rejet</label>
          <textarea
            value={rejectComment}
            onChange={e => setRejectComment(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
            placeholder="Expliquez les modifications à apporter..."
          />
          <div className="flex gap-2 mt-2">
            <button
              onClick={reject}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg"
            >
              Confirmer le rejet
            </button>
            <button onClick={() => setShowReject(false)} className="text-sm text-gray-600 px-3 py-2">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Formulaire d'édition des contacts */}
      {showEdit && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-blue-800 mb-3">Modifier les contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Entreprise</p>
              <input type="email" placeholder="Email entreprise" value={editFields.companyEmail}
                onChange={e => setEditFields(f => ({ ...f, companyEmail: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="text" placeholder="Téléphone entreprise" value={editFields.companyPhone}
                onChange={e => setEditFields(f => ({ ...f, companyPhone: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Tuteur entreprise</p>
              <input type="text" placeholder="Prénom tuteur" value={editFields.tutorFirstName}
                onChange={e => setEditFields(f => ({ ...f, tutorFirstName: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="text" placeholder="Nom tuteur" value={editFields.tutorName}
                onChange={e => setEditFields(f => ({ ...f, tutorName: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="email" placeholder="Email tuteur" value={editFields.tutorEmail}
                onChange={e => setEditFields(f => ({ ...f, tutorEmail: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="text" placeholder="Téléphone tuteur" value={editFields.tutorPhone}
                onChange={e => setEditFields(f => ({ ...f, tutorPhone: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Durée du stage</p>
              <input type="number" placeholder="Heures hebdomadaires" value={editFields.weeklyHours}
                onChange={e => setEditFields(f => ({ ...f, weeklyHours: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="number" placeholder="Nombre de jours total" value={editFields.totalDays}
                onChange={e => setEditFields(f => ({ ...f, totalDays: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1.5">Représentant légal</p>
              <input type="text" placeholder="Prénom représentant" value={editFields.repFirstName}
                onChange={e => setEditFields(f => ({ ...f, repFirstName: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="text" placeholder="Nom représentant" value={editFields.repName}
                onChange={e => setEditFields(f => ({ ...f, repName: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm mb-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400" />
              <input type="text" placeholder="Téléphone représentant" value={editFields.repPhone}
                onChange={e => setEditFields(f => ({ ...f, repPhone: e.target.value }))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-xs font-medium text-gray-500 mb-1.5">Horaires</p>
            <textarea
              rows={3}
              placeholder="Ex : Lundi-Vendredi 8h-12h / 14h-17h"
              value={editFields.schedule || ''}
              onChange={e => setEditFields(f => ({ ...f, schedule: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={saveEdit} disabled={actionLoading}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-2 rounded-lg">
              Enregistrer
            </button>
            <button onClick={() => setShowEdit(false)} className="text-sm text-gray-600 px-3 py-2">
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Modale de confirmation suppression */}
      {showDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer la convention</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cette action est irréversible. Tapez <span className="font-mono font-bold text-red-600">supprimer</span> pour confirmer.
            </p>
            <input
              type="text"
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="supprimer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-4"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDelete(false)}
                className="text-sm text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                onClick={deleteConvention}
                disabled={deleteConfirm !== 'supprimer' || actionLoading}
                className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Détails */}
      <div className="space-y-4">
        <Section title="Étudiant">
          <Row label="Nom">{c.student?.civilite && `${c.student.civilite} `}{c.student?.lastName} {c.student?.firstName}</Row>
          <Row label="Email">{c.student?.email}</Row>
          {c.student?.phone && <Row label="Tél.">{c.student.phone}</Row>}
          {c.student?.dateNaissance && <Row label="Naissance">{new Date(c.student.dateNaissance).toLocaleDateString('fr-FR')}</Row>}
          {c.student?.adresse && <Row label="Adresse">{c.student.adresse}</Row>}
          <Row label="Classe">{c.classe?.name}{c.classe?.schoolYear ? ` — ${c.classe.schoolYear.label}` : ''}</Row>
          {c.stagePeriode && <Row label="Période">{c.stagePeriode.label}</Row>}
        </Section>

        <Section title="Entreprise">
          <Row label="Nom">{c.company?.name}</Row>
          <Row label="SIRET">{c.company?.siret}</Row>
          <Row label="Adresse">{c.company?.address}, {c.company?.zipCode} {c.company?.city}</Row>
          {c.company?.apeCode && <Row label="APE">{c.company.apeCode}</Row>}
          {c.company?.activityDomain && <Row label="Domaine">{c.company.activityDomain}</Row>}
          {c.company?.phone && <Row label="Tél.">{c.company.phone}</Row>}
          {c.company?.email && <Row label="Email">{c.company.email}</Row>}
          {c.assureur && <Row label="Assureur">{c.assureur}</Row>}
          {c.contratAssurance && <Row label="N° contrat">{c.contratAssurance}</Row>}
        </Section>

        <Section title="Représentant légal">
          <Row label="Nom">{c.repCivilite && `${c.repCivilite} `}{c.repFirstName} {c.repName}</Row>
          <Row label="Fonction">{c.repFunction}</Row>
          <Row label="Tél.">{c.repPhone}</Row>
        </Section>

        <Section title="Tuteur entreprise">
          <Row label="Nom">{c.tutorCivilite && `${c.tutorCivilite} `}{c.tutorFirstName} {c.tutorName}</Row>
          <Row label="Fonction">{c.tutorFunction}</Row>
          <Row label="Email">{c.tutorEmail}</Row>
          <Row label="Tél.">{c.tutorPhone}</Row>
        </Section>

        {c.isMineur && (
          <Section title="Représentant légal de l'étudiant (mineur)">
            <Row label="Nom">{c.repLegalPrenom} {c.repLegalNom}</Row>
            {c.repLegalLien && <Row label="Lien">{c.repLegalLien}</Row>}
            {c.repLegalEmail && <Row label="Email">{c.repLegalEmail}</Row>}
            {c.repLegalTelephone && <Row label="Tél.">{c.repLegalTelephone}</Row>}
          </Section>
        )}

        <Section title="Stage">
          <Row label="Début">{c.startDate ? new Date(c.startDate).toLocaleDateString('fr-FR') : '?'}</Row>
          <Row label="Fin">{c.endDate ? new Date(c.endDate).toLocaleDateString('fr-FR') : '?'}</Row>
          <Row label="Durée">{c.totalDays ? `${c.totalDays} jours` : '—'} · {c.weeklyHours ? `${c.weeklyHours}h/semaine` : '—'}</Row>
          {c.gratification && <Row label="Gratification">{c.gratification} €/{c.gratificationPeriode || 'h'}</Row>}
          {c.avantagesPrive && <Row label="Avantages privé">{c.avantagesPrive}</Row>}
          {c.avantagesPublic && <Row label="Avantages public">{c.avantagesPublic}</Row>}
          {c.casParticuliers && <Row label="Cas particuliers">{c.casParticuliers}</Row>}
          {c.classe?.conges && <Row label="Congés">{c.classe.conges}</Row>}
          {c.schedule && (typeof c.schedule === 'string' ? c.schedule.trim() : Object.keys(c.schedule).length > 0) && (
            <div className="flex gap-2 text-sm mb-1.5">
              <span className="font-medium text-gray-500 w-28 shrink-0">Horaires :</span>
              <span className="text-gray-800 whitespace-pre-wrap">
                {typeof c.schedule === 'string'
                  ? c.schedule
                  : ['lundi','mardi','mercredi','jeudi','vendredi','samedi']
                      .filter(j => c.schedule[j]?.matin || c.schedule[j]?.apmidi)
                      .map(j => {
                        const s = c.schedule[j];
                        const parts = [];
                        if (s.matin) parts.push(`matin: ${s.matin}`);
                        if (s.apmidi) parts.push(`ap-midi: ${s.apmidi}`);
                        return `${j.charAt(0).toUpperCase() + j.slice(1)}: ${parts.join(', ')}`;
                      }).join(' | ') || '—'}
              </span>
            </div>
          )}
        </Section>

        <Section title="Modalités pédagogiques">
          {c.missions && <><p className="text-xs font-medium text-gray-500 mb-1">Missions</p><p className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{c.missions}</p></>}
          {c.skills && <><p className="text-xs font-medium text-gray-500 mb-1">Compétences visées</p><p className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{c.skills}</p></>}
          {c.classe?.modalitesEncadrement && <><p className="text-xs font-medium text-gray-500 mb-1">Modalités d'encadrement</p><p className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{c.classe.modalitesEncadrement}</p></>}
          {c.classe?.modalitesEvaluation && <><p className="text-xs font-medium text-gray-500 mb-1">Modalités d'évaluation</p><p className="text-sm text-gray-600 whitespace-pre-wrap mb-3">{c.classe.modalitesEvaluation}</p></>}
          {c.classe?.travailAFournir && <><p className="text-xs font-medium text-gray-500 mb-1">Travail à fournir</p><p className="text-sm text-gray-600 whitespace-pre-wrap">{c.classe.travailAFournir}</p></>}
        </Section>


        {c.documents?.length > 0 && (
          <Section title={`Pièces jointes (${c.documents.length})`}>
            <ul className="space-y-1">
              {c.documents.map(d => (
                <li key={d.id} className="text-sm text-gray-600">{d.fileName}</li>
              ))}
            </ul>
          </Section>
        )}

        {c.appreciation && (
          <Section title="Appréciation du tuteur">
            {c.appreciation.rating && <Row label="Note">{c.appreciation.rating}/5</Row>}
            <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{c.appreciation.comment}</p>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-medium text-gray-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-2 text-sm mb-1.5">
      <span className="font-medium text-gray-500 w-28 shrink-0">{label} :</span>
      <span className="text-gray-800">{children}</span>
    </div>
  );
}
